import { parseTypographyStyleClaim, toDisplayFontFamilyName } from "./typography.ts";

export type BrandAvatarVisualIdentity = {
  colorPalette?: string[] | null;
  secondaryColorPalette?: string[] | null;
  typographyStyle?: unknown;
};

export type BrandAvatarStyle = {
  className: string;
  style?: {
    backgroundColor?: string;
    backgroundImage?: string;
    borderColor?: string;
    color?: string;
    fontFamily?: string;
  };
};

const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

const trimToNull = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

export const getBrandInitials = (label: string | null | undefined): string => {
  const normalized =
    trimToNull(label)
      ?.replace(/^www\./i, "")
      .replace(/\.[a-z]{2,}$/i, "") ?? "";
  const parts = normalized.split(/[^a-z0-9]+/i).filter(Boolean);

  return (parts[0]?.[0] || "B").toUpperCase();
};

const normalizeAvatarColor = (value: string | null | undefined): string | null => {
  const trimmed = trimToNull(value);
  return trimmed && HEX_COLOR_PATTERN.test(trimmed) ? trimmed.toUpperCase() : null;
};

const getContrastTextColor = (backgroundColor: string): string => {
  const hex = backgroundColor.slice(1);
  const expandedHex =
    hex.length === 3
      ? hex
          .split("")
          .map((value) => `${value}${value}`)
          .join("")
      : hex;
  const red = Number.parseInt(expandedHex.slice(0, 2), 16);
  const green = Number.parseInt(expandedHex.slice(2, 4), 16);
  const blue = Number.parseInt(expandedHex.slice(4, 6), 16);
  const luminance = (red * 299 + green * 587 + blue * 114) / 1000;

  return luminance >= 160 ? "#132231" : "#FFFFFF";
};

const sanitizeAvatarFontFamily = (value: string): string =>
  toDisplayFontFamilyName(value)
    .replace(/[^a-z0-9 -]/gi, "")
    .trim();

const getAvatarFontFamily = (brand: BrandAvatarVisualIdentity): string | null => {
  const typography = parseTypographyStyleClaim(brand.typographyStyle);
  const preferredFamily =
    typography?.families.find((family) => family.role === "headings") ?? typography?.families[0];
  const fontFamily = trimToNull(preferredFamily?.family);

  if (!fontFamily) {
    return null;
  }

  const sanitized = sanitizeAvatarFontFamily(fontFamily);
  return sanitized ? `"${sanitized}", ui-sans-serif, system-ui, sans-serif` : null;
};

export const getBrandAvatarStyle = (brand: BrandAvatarVisualIdentity): BrandAvatarStyle => {
  const primaryColor = normalizeAvatarColor(brand.colorPalette?.[0]);
  const secondaryColor =
    normalizeAvatarColor(brand.secondaryColorPalette?.[0]) ??
    normalizeAvatarColor(brand.colorPalette?.[1]);

  if (!primaryColor) {
    return {
      className: "border-transparent bg-primary text-primary-foreground",
    };
  }

  return {
    className: "border-transparent",
    style: {
      backgroundColor: primaryColor,
      backgroundImage: secondaryColor
        ? `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
        : undefined,
      borderColor: secondaryColor ?? primaryColor,
      color: getContrastTextColor(primaryColor),
      fontFamily: getAvatarFontFamily(brand) ?? undefined,
    },
  };
};
